import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const ContactPage = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Contact Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    request_type: 'support',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  
  // Chat State
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: 'Bonjour! Je suis ELYA, votre assistante IA. Comment puis-je vous aider aujourd\'hui? 🌟',
      timestamp: new Date()
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Handle form input change
  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingForm(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, formData);
      
      setFormStatus({
        type: 'success',
        message: response.data.message
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        request_type: 'support',
        message: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ type: '', message: '' });
      }, 5000);
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Erreur lors de l\'envoi du formulaire. Veuillez réessayer.'
      });
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // Handle chat message send
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isSendingMessage) return;

    const userMessage = {
      role: 'user',
      content: chatInput,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsSendingMessage(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/assistant/chat`, {
        session_id: sessionId,
        message: chatInput,
        user_email: null,
        user_plan: 'guest'
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.reply,
        timestamp: new Date(response.data.timestamp)
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Désolée, j\'ai rencontré une erreur. Veuillez réessayer. 😔',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSendingMessage(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E1117] via-[#1A1C23] to-[#0E1117] text-white">
      {/* Header */}
      <header className="border-b border-yellow-400/20 bg-[#0E1117]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <span className="text-[#0E1117] font-bold text-xl">M</span>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
              Coach DIA
            </span>
          </motion.div>
          
          <button
            onClick={() => navigate(-1)}
            data-testid="contact-back-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold
                       text-yellow-400 bg-yellow-400/10 border border-yellow-400/40
                       shadow-[0_1px_3px_rgba(0,0,0,0.25),0_4px_14px_rgba(250,204,21,0.12)]
                       hover:bg-yellow-400/20 hover:border-yellow-400/60
                       hover:shadow-[0_2px_6px_rgba(250,204,21,0.25),0_6px_20px_rgba(250,204,21,0.15)]
                       active:scale-95 transition-all duration-150"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Back button mobile-visible duplicate (fluid UX) */}
        <button
          onClick={() => navigate(-1)}
          data-testid="contact-back-btn-inline"
          className="md:hidden inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full text-sm font-semibold
                     text-yellow-400 bg-yellow-400/10 border border-yellow-400/40
                     hover:bg-yellow-400/20 active:scale-95 transition-all duration-150"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-300">
            Posez vos questions à ELYA ou envoyez-nous un message
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Left: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1A1C23]/50 backdrop-blur-lg rounded-2xl p-8 border border-yellow-400/20 shadow-[0_0_30px_rgba(255,216,111,0.1)]"
          >
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <span className="text-3xl">📧</span>
              Formulaire de Contact
            </h2>

            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Nom complet
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  disabled={isSubmittingForm}
                  className="w-full px-4 py-3 bg-[#0E1117] border border-yellow-400/30 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors disabled:opacity-50"
                  placeholder="Votre nom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  disabled={isSubmittingForm}
                  className="w-full px-4 py-3 bg-[#0E1117] border border-yellow-400/30 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors disabled:opacity-50"
                  placeholder="votre.email@exemple.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Type de demande
                </label>
                <select
                  name="request_type"
                  value={formData.request_type}
                  onChange={handleFormChange}
                  disabled={isSubmittingForm}
                  className="w-full px-4 py-3 bg-[#0E1117] border border-yellow-400/30 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors disabled:opacity-50"
                >
                  <option value="support">Support Technique</option>
                  <option value="billing">Facturation</option>
                  <option value="partnership">Partenariat</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  required
                  disabled={isSubmittingForm}
                  rows="6"
                  className="w-full px-4 py-3 bg-[#0E1117] border border-yellow-400/30 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors resize-none disabled:opacity-50"
                  placeholder="Décrivez votre demande en détail..."
                />
              </div>

              {formStatus.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg border ${
                    formStatus.type === 'success'
                      ? 'bg-green-500/10 border-green-500/30 text-green-400'
                      : 'bg-red-500/10 border-red-500/30 text-red-400'
                  }`}
                >
                  {formStatus.message}
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isSubmittingForm}
                className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-[#0E1117] font-bold rounded-lg hover:shadow-[0_0_30px_rgba(255,216,111,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingForm ? 'Envoi en cours...' : 'Envoyer le message'}
              </button>
            </form>
          </motion.div>

          {/* Right: ELYA AI Assistant Chat */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#1A1C23]/50 backdrop-blur-lg rounded-2xl p-8 border border-yellow-400/20 shadow-[0_0_30px_rgba(255,216,111,0.1)] flex flex-col h-[700px]"
          >
            {/* ELYA Header */}
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-yellow-400/20">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                  <span className="text-3xl">🤖</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-[#1A1C23]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">ELYA</h2>
                <p className="text-sm text-gray-400">Assistante IA • En ligne</p>
              </div>
            </div>

            {/* Chat Messages */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto mb-4 space-y-4 pr-2 scrollbar-thin scrollbar-thumb-yellow-400/20 scrollbar-track-transparent"
            >
              <AnimatePresence>
                {chatMessages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-[#0E1117]'
                          : 'bg-[#0E1117] border border-yellow-400/30'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-[#0E1117]/60' : 'text-gray-500'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {isSendingMessage && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-[#0E1117] border border-yellow-400/30 px-4 py-3 rounded-2xl">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                disabled={isSendingMessage}
                placeholder="Posez votre question à ELYA..."
                className="flex-1 px-4 py-3 bg-[#0E1117] border border-yellow-400/30 rounded-lg focus:outline-none focus:border-yellow-400 transition-colors disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isSendingMessage || !chatInput.trim()}
                className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-[#0E1117] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(255,216,111,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-xl">➤</span>
              </button>
            </form>

            {/* Quick Questions */}
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                'Comment accéder aux modules premium?',
                'Quels sont les tarifs?',
                'Comment fonctionne le coach vocal?'
              ].map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setChatInput(question);
                  }}
                  disabled={isSendingMessage}
                  className="px-3 py-1 text-xs bg-yellow-400/10 hover:bg-yellow-400/20 border border-yellow-400/30 rounded-full transition-colors disabled:opacity-50"
                >
                  {question}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
